/**
 * ChartZoomControls (#2436) — the presentational, fully-keyboard-operable
 * toolbar of zoom/pan affordances shared by the three app charts: pan earlier
 * (←), zoom out (−), zoom in (+), pan later (→), and reset/"fit". These are the
 * discoverable + accessible ALTERNATES to the primary wheel/drag gestures (the
 * issue: don't make zoom/pan mouse-only) — every action reachable by Tab + Enter
 * with a German ``aria-label``. Buttons disable when their capability is
 * unavailable (at a bound / already fit) so the state is legible, never a dead
 * click. Stateless: the ``useChartTimeWindow`` hook owns the window; this only
 * renders flags + fires callbacks.
 */
import { ChevronLeft, ChevronRight, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import './ChartZoomControls.css';

export interface ChartZoomControlsProps {
  canZoomIn: boolean;
  canZoomOut: boolean;
  canPanPast: boolean;
  canPanFuture: boolean;
  /** True when the window already sits at the default (fit) view. */
  isDefault: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onPanPast: () => void;
  onPanFuture: () => void;
  onReset: () => void;
}

export default function ChartZoomControls({
  canZoomIn,
  canZoomOut,
  canPanPast,
  canPanFuture,
  isDefault,
  onZoomIn,
  onZoomOut,
  onPanPast,
  onPanFuture,
  onReset,
}: ChartZoomControlsProps) {
  return (
    <div className="chart-zoom-controls" role="group" aria-label="Diagramm zoomen und verschieben">
      <button
        type="button"
        className="chart-zoom-controls__btn"
        aria-label="Früher"
        title="Früher (←)"
        disabled={!canPanPast}
        onClick={onPanPast}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="chart-zoom-controls__btn"
        aria-label="Herauszoomen"
        title="Herauszoomen (−)"
        disabled={!canZoomOut}
        onClick={onZoomOut}
      >
        <ZoomOut size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="chart-zoom-controls__btn"
        aria-label="Hineinzoomen"
        title="Hineinzoomen (+)"
        disabled={!canZoomIn}
        onClick={onZoomIn}
      >
        <ZoomIn size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="chart-zoom-controls__btn"
        aria-label="Später"
        title="Später (→)"
        disabled={!canPanFuture}
        onClick={onPanFuture}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="chart-zoom-controls__btn chart-zoom-controls__btn--reset"
        aria-label="Ansicht zurücksetzen"
        title="Ansicht zurücksetzen (Pos1)"
        disabled={isDefault}
        onClick={onReset}
      >
        <Maximize2 size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
